import React, { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Film, Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useDebounce } from "@/hooks/useDebounce";
import { client } from "@/services/api/client.gen";
import { cn } from "@/lib/utils";
import type { SearchResponse, SearchResultItem } from "@/types/search";

/**
 * A production the ticket can be scoped to. `auth` (when present) carries the
 * caller's per-item permissions, used by the consent gate to decide whether the
 * support-access checkbox may be enabled.
 */
export interface SelectedProduction {
  id: string;
  name: string;
  auth?: Record<string, { has: boolean }>;
}

interface ProductionPickerProps {
  value: SelectedProduction | null;
  onChange: (production: SelectedProduction | null) => void;
}

/**
 * Debounced typeahead for scoping a support ticket to one of the caller's
 * productions. Modeled on `TeamSharePicker`, but backed by a CONTROLLED
 * `GET /v1/search` query (`types=project`) rather than the URL-synced
 * `useSearch` hook — so it never mutates the page URL and is safe to mount
 * inside a modal. Deliberately NOT backed by the cached `appStore.projects`
 * list: a user may have tens of thousands of productions, and the current one
 * pre-fills from a known `{ id, name }`.
 */
export const ProductionPicker: React.FC<ProductionPickerProps> = ({
  value,
  onChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const debounced = useDebounce(searchTerm, 300);
  const trimmed = debounced.trim();

  const { data: results = [], isFetching } = useQuery<SearchResultItem[]>({
    queryKey: ["support-production-search", trimmed],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("q", trimmed);
      params.set("types", "project");
      params.set("limit", "8");
      const response = await client.get<SearchResponse>({
        url: `/v1/search?${params.toString()}`,
        security: [{ scheme: "bearer", type: "http" }],
      });
      const data = (response.data as SearchResponse | undefined)?.data ?? [];
      return data.filter((r) => r.entityType === "project");
    },
    // No call until the user has typed something.
    enabled: trimmed.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });

  // Selected state: show the chosen production with a way to change it.
  if (value) {
    return (
      <div className="flex items-center gap-3 rounded-lg border px-3 py-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Film className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          {/* line-clamp (wrap → ellipsis after 2 lines) rather than `truncate`:
              nowrap truncation makes the name's min-content the whole string,
              which overrides the dialog's max-width and stretches the modal.
              Wrapping keeps min-content = longest word, so a long production
              name stays inside the 480px form. */}
          <div className="line-clamp-2 break-words text-sm font-medium text-foreground">
            {value.name}
          </div>
          <div className="text-xs text-muted-foreground">
            Selected production
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="shrink-0"
          onClick={() => {
            setSearchTerm("");
            onChange(null);
          }}
        >
          Change
        </Button>
      </div>
    );
  }

  // Search state: debounced input + a FLOATING results overlay. The dropdown is
  // absolutely positioned under the input (z-indexed above the form) so the rest
  // of the form never shifts/expands while searching. Shown only while the input
  // is focused; `onMouseDown` preventDefault on the rows lets a click select a
  // result before the input's blur closes the overlay.
  return (
    <div className="relative">
      <div className="relative">
        {isFetching ? (
          <Loader2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        ) : (
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        )}
        <Input
          type="text"
          placeholder="Search your productions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className="pl-9"
          aria-label="Search productions"
        />
      </div>

      {isFocused && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-md border bg-popover shadow-md">
          {trimmed.length === 0 ? (
            <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
              Type to search your productions
            </div>
          ) : results.length === 0 && !isFetching ? (
            <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
              No productions found
            </div>
          ) : (
            results.map((result) => (
              <button
                key={result.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() =>
                  onChange({
                    id: result.id,
                    name: result.name ?? "Untitled production",
                    auth: result.auth,
                  })
                }
                className={cn(
                  "flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors",
                  "cursor-pointer hover:bg-accent/50",
                )}
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10">
                  {result.thumbnailUrl ? (
                    <img
                      src={result.thumbnailUrl}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Film className="h-4 w-4 text-primary" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">
                    {result.name ?? "Untitled production"}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
